import { FrontendConstants } from '../common/FrontendConstants';
import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import { CommonConstants } from '@shared/common';
import type { ICouponDTO, IInvoiceDTO, IPlanDTO, ISubscriptionDTO, LocalizationCode } from '@shared/common';

/** Request to start a Stripe checkout session for a tenant on a given plan. */
export interface ICheckoutRequest {
  tenantID: string;
  planCode: string;
}

/** Per-meter pricing entry submitted when creating a plan. */
export interface IPlanMeterPriceRequest {
  meterKey: string;
  includedQuantity: number;
  unitAmount: number;
  unitAmountByCurrency?: Record<string, number>;
}

/** Request to create a billing plan (Portal operator only). Provider catalog ids (product, base price, metered prices) are assigned by the billing service on sync, never supplied by the caller. */
export interface ICreatePlanRequest {
  code: string;
  name: string;
  description?: string;
  tagline?: string;
  features?: string[];
  currency?: string;
  baseAmount: number;
  baseAmountByCurrency?: Record<string, number>;
  setupAmount?: number;
  setupAmountByCurrency?: Record<string, number>;
  concurrentLimit?: number;
  meterPrices?: IPlanMeterPriceRequest[];
  active?: boolean;
  featured?: boolean;
}

/**
 * Request to update a plan's provider-irrelevant fields (Portal operator only). Synced catalog fields (code, name, description, currency, base/meter amounts, included quantities) are immutable
 * post-create and are never accepted here; only these provider-irrelevant fields can change. Every field is optional, but at least one must be present.
 */
export interface IUpdatePlanDetailsRequest {
  active?: boolean;
  featured?: boolean;
  tagline?: string;
  features?: string[];
  setupAmount?: number;
  setupAmountByCurrency?: Record<string, number>;
}

/** Provider-hosted redirect URL returned by checkout/portal session creation. */
export interface IHostedURLResponse {
  url: string;
}

/** Per-meter usage total within a billing period. */
export interface IUsageMeterTotalDTO {
  meterKey: string;
  quantity: number;
}

/** Tenant usage summary for the current billing period. */
export interface IUsageSummaryDTO {
  tenantID: string;
  periodStartISO: string;
  periodEndISO: string;
  totals: IUsageMeterTotalDTO[];
}

/** Billing access decision derived from a tenant's subscription state. */
export interface IAccessDecisionDTO {
  allowAI: boolean;
  allowForwarding: boolean;
  status: string | null;
  inGracePeriod: boolean;
  graceEndsAt?: string;
  reason: string;
}

/** Margin report comparing billed revenue against provider cost for a tenant period. */
export interface IMarginReportDTO {
  tenantID: string;
  periodStartISO: string;
  periodEndISO: string;
  billedTotal: number;
  providerCostTotal: number;
  margin: number;
}

/**
 * Client for billing: tenant-facing checkout/portal/subscription/usage reads and cross-tenant admin oversight.
 *
 * All calls route through the billing service (`gated/...` for tenants, `admin/...` for Portal operators).
 */
export class BillingClient {
  /** Singleton instance. */
  private static instance: BillingClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton BillingClient instance. */
  public static GetInstance(): BillingClient {
    return BillingClient.instance ?? (BillingClient.instance = new BillingClient());
  }

  /**
   * POST /api/billing/gated/checkout-session - Start a Stripe checkout session.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param body - The checkout request (tenant + plan code).
   * @returns The hosted checkout URL to redirect the user to.
   */
  public async CreateCheckoutSession(signal: AbortSignal, body: ICheckoutRequest): Promise<IHostedURLResponse> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      'gated/checkout-session',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to create checkout session');
    return await res.json();
  }

  /**
   * POST /api/billing/gated/portal-session - Open the Stripe billing portal for a tenant.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @returns The hosted billing-portal URL to redirect the user to.
   */
  public async CreatePortalSession(signal: AbortSignal, tenantID: string): Promise<IHostedURLResponse> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      'gated/portal-session',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify({ tenantID }),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to open billing portal');
    return await res.json();
  }

  /**
   * GET /api/billing/gated/subscription/:tenantID - Fetch a tenant's subscription.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @returns The subscription, or null when none exists.
   */
  public async GetSubscription(signal: AbortSignal, tenantID: string): Promise<ISubscriptionDTO | null> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `gated/subscription/${tenantID}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch subscription');
    return await res.json();
  }

  /**
   * GET /api/billing/gated/current-plan/:tenantID - Fetch the full plan a tenant is currently subscribed to (base/setup/meter pricing, features), resolved by id so it stays visible even after
   * the plan is retired from the public catalog.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param lang - Target language for plan display copy (name, tagline, features). Defaults to the source language.
   * @returns The current plan, or null when the tenant has no subscription.
   */
  public async GetCurrentPlan(signal: AbortSignal, tenantID: string, lang: LocalizationCode = CommonConstants.Defaults.Localization): Promise<IPlanDTO | null> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const params = new URLSearchParams({ [CommonConstants.Params.Lang]: lang });
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `gated/current-plan/${tenantID}?${params}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch current plan');
    return await res.json();
  }

  /**
   * GET /api/billing/gated/summary/:tenantID - Fetch a tenant's current-period usage summary.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @returns The usage summary.
   */
  public async GetSummary(signal: AbortSignal, tenantID: string): Promise<IUsageSummaryDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `gated/summary/${tenantID}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch usage summary');
    return await res.json();
  }

  /**
   * GET /api/billing/gated/invoices/:tenantID - List a tenant's invoices.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @returns The tenant's invoices, most recent first.
   */
  public async GetInvoices(signal: AbortSignal, tenantID: string): Promise<IInvoiceDTO[]> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `gated/invoices/${tenantID}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch invoices');
    return await res.json();
  }

  /**
   * GET /api/billing/gated/access/:tenantID - Fetch a tenant's billing access decision.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @returns The access decision derived from the tenant's subscription state.
   */
  public async GetAccess(signal: AbortSignal, tenantID: string): Promise<IAccessDecisionDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `gated/access/${tenantID}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch billing access');
    return await res.json();
  }

  /**
   * POST /api/billing/gated/refresh/:tenantID - Reconcile against the billing provider on demand, then return the fresh access decision. Recovery path for a missed subscription/invoice webhook:
   * a tenant who has just paid or updated their card can call this to unblock their AI without waiting for the provider to redeliver.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @returns The freshly reconciled access decision.
   */
  public async RefreshAccess(signal: AbortSignal, tenantID: string): Promise<IAccessDecisionDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      `gated/refresh/${tenantID}`,
      { method: FrontendConstants.MethodPost, headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson }, signal },
      auth
    );
    if (!res.ok) throw new Error('Failed to refresh billing access');
    return await res.json();
  }

  /**
   * GET /api/billing/admin/subscriptions - List all subscriptions (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns All subscriptions.
   */
  public async ListAdminSubscriptions(signal: AbortSignal): Promise<ISubscriptionDTO[]> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, 'admin/subscriptions', { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch subscriptions');
    return await res.json();
  }

  /**
   * GET /api/billing/admin/plans - List all billing plans (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns All plans.
   */
  public async ListAdminPlans(signal: AbortSignal): Promise<IPlanDTO[]> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, 'admin/plans', { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch plans');
    return await res.json();
  }

  /**
   * GET /api/billing/admin/clear/billing - Flush the plan and subscription Redis caches (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns 'CLEARED' confirmation string.
   */
  public async ClearBillingOversight(signal: AbortSignal): Promise<string> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, 'admin/clear/billing', { signal }, auth);
    if (!res.ok) throw new Error('Failed to clear billing cache');
    return await res.text();
  }

  /**
   * POST /api/billing/admin/knowledge/verify - Queue an embedding pass for every tenant and core knowledge document that has chunks but no embeddings (Portal operator only). Idempotent: a
   * document already queued (from a prior click or an edit-save) is not re-queued.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns The number of documents newly queued for embedding.
   */
  public async VerifyKnowledge(signal: AbortSignal): Promise<{ queued: number }> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, 'admin/knowledge/verify', { method: FrontendConstants.MethodPost, signal }, auth);
    if (!res.ok) throw new Error('Failed to verify knowledge documents');
    return (await res.json()) as { queued: number };
  }

  /**
   * GET /api/billing/anon/plans - Public catalog of active plans for the marketing landing (no auth).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param lang - Target language for plan display copy. Defaults to the source language.
   * @returns The active plans, ascending by base amount.
   */
  public async ListPublicPlans(signal: AbortSignal, lang: LocalizationCode = CommonConstants.Defaults.Localization): Promise<IPlanDTO[]> {
    const params = new URLSearchParams({ [CommonConstants.Params.Lang]: lang });
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `anon/plans?${params}`, { signal });
    if (!res.ok) throw new Error('Failed to fetch plans');
    return await res.json();
  }

  /**
   * POST /api/billing/admin/plans - Create a billing plan (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param body - The plan creation payload.
   * @returns The created plan.
   */
  public async CreateAdminPlan(signal: AbortSignal, body: ICreatePlanRequest): Promise<IPlanDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      'admin/plans',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to create plan');
    return await res.json();
  }

  /**
   * PUT /api/billing/admin/plans/:planID - Update a plan's provider-irrelevant fields (Portal operator only).
   *
   * Only provider-irrelevant fields are accepted: active, featured, tagline, features, and the one-time setup fee with its per-currency overrides. The server ignores any other field, so synced
   * catalog values (code, name, pricing, included quantities) can never be changed through this call.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param planID - The plan to update.
   * @param body - The provider-irrelevant fields to change.
   * @returns The updated plan.
   */
  public async UpdateAdminPlanDetails(signal: AbortSignal, planID: string, body: IUpdatePlanDetailsRequest): Promise<IPlanDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      `admin/plans/${planID}`,
      {
        method: FrontendConstants.MethodPut,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to update plan');
    return await res.json();
  }

  /**
   * DELETE /api/billing/admin/plans/:planID - Delete a plan (Portal operator only). Rejected when subscriptions reference the plan.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param planID - The plan to delete.
   * @returns An object indicating whether a row was deleted.
   */
  public async DeleteAdminPlan(signal: AbortSignal, planID: string): Promise<{ deleted: boolean }> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `admin/plans/${planID}`, { method: FrontendConstants.MethodDelete, signal }, auth);
    if (!res.ok) throw new Error('Failed to delete plan');
    return await res.json();
  }

  /**
   * POST /api/billing/admin/subscriptions/assign - Switch a tenant's plan on the billing provider (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant to switch.
   * @param planID - The plan to switch onto.
   * @param couponCode - Desired whole-subscription coupon: omit to leave any existing coupon untouched, pass an empty string to clear it, or a configured coupon code to attach it.
   * @returns The reconciled subscription.
   */
  public async AssignTenantPlan(signal: AbortSignal, tenantID: string, planID: string, couponCode?: string): Promise<ISubscriptionDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      'admin/subscriptions/assign',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify({ tenantID, planID, couponCode }),
        signal
      },
      auth
    );
    // Surface the server's reason (e.g. "must complete checkout before a plan can be switched") so the operator sees why a switch was blocked, not a generic failure.
    if (!res.ok) {
      const detail = (await res.json().catch(() => null)) as { message?: unknown } | null;
      throw new Error(typeof detail?.message === 'string' ? detail.message : 'Failed to assign plan');
    }
    return await res.json();
  }

  /**
   * POST /api/billing/admin/coupons - Create a whole-subscription coupon (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param code - The operator-facing coupon code.
   * @param percentOff - The whole-number percent off (1-100).
   * @returns The updated coupon map (code to percent off + provider coupon id).
   */
  public async CreateCoupon(signal: AbortSignal, code: string, percentOff: number): Promise<Record<string, ICouponDTO>> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      'admin/coupons',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify({ code, percentOff }),
        signal
      },
      auth
    );
    if (!res.ok) {
      const detail = (await res.json().catch(() => null)) as { message?: unknown } | null;
      throw new Error(typeof detail?.message === 'string' ? detail.message : 'Failed to create coupon');
    }
    return await res.json();
  }

  /**
   * DELETE /api/billing/admin/coupons/:code - Delete a whole-subscription coupon (Portal operator only). Rejected when a tenant still has the coupon assigned.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param code - The coupon code to delete.
   * @returns The updated coupon map after removal.
   */
  public async DeleteCoupon(signal: AbortSignal, code: string): Promise<Record<string, ICouponDTO>> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `admin/coupons/${encodeURIComponent(code)}`, { method: FrontendConstants.MethodDelete, signal }, auth);
    if (!res.ok) {
      const detail = (await res.json().catch(() => null)) as { message?: unknown } | null;
      throw new Error(typeof detail?.message === 'string' ? detail.message : 'Failed to delete coupon');
    }
    return await res.json();
  }

  /**
   * POST /api/billing/admin/subscriptions/cancel - Schedule a tenant's subscription to cancel at the end of the current billing period (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The tenant whose subscription is canceled.
   * @returns The reconciled (or unchanged) subscription.
   */
  public async AdminCancelTenantSubscription(signal: AbortSignal, tenantID: string): Promise<ISubscriptionDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      'admin/subscriptions/cancel',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify({ tenantID }),
        signal
      },
      auth
    );
    // Surface the server's reason (e.g. "must complete checkout") so the operator sees why the cancel was blocked, not a generic failure.
    if (!res.ok) {
      const detail = (await res.json().catch(() => null)) as { message?: unknown } | null;
      throw new Error(typeof detail?.message === 'string' ? detail.message : 'Failed to cancel subscription');
    }
    return await res.json();
  }

  /**
   * POST /api/billing/gated/subscription/:tenantID/plan - Switch the tenant's subscription onto a different plan (self-service, immediate + prorated; clears a pending cancel).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @param planID - The plan to switch onto.
   * @returns The reconciled subscription.
   */
  public async ChangeTenantPlan(signal: AbortSignal, tenantID: string, planID: string): Promise<ISubscriptionDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      `gated/subscription/${tenantID}/plan`,
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify({ planID }),
        signal
      },
      auth
    );
    // Surface the server's reason (e.g. "complete checkout first", "plan not available") so the tenant sees why the switch was blocked, not a generic failure.
    if (!res.ok) {
      const detail = (await res.json().catch(() => null)) as { message?: unknown } | null;
      throw new Error(typeof detail?.message === 'string' ? detail.message : 'Failed to change plan');
    }
    return await res.json();
  }

  /**
   * POST /api/billing/gated/subscription/:tenantID/cancel - Schedule the tenant's subscription to cancel at the end of the current billing period (self-service).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @returns The reconciled (or unchanged) subscription.
   */
  public async CancelTenantSubscription(signal: AbortSignal, tenantID: string): Promise<ISubscriptionDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `gated/subscription/${tenantID}/cancel`, { method: FrontendConstants.MethodPost, signal }, auth);
    // Surface the server's reason so the tenant sees why the cancel was blocked, not a generic failure.
    if (!res.ok) {
      const detail = (await res.json().catch(() => null)) as { message?: unknown } | null;
      throw new Error(typeof detail?.message === 'string' ? detail.message : 'Failed to cancel subscription');
    }
    return await res.json();
  }

  /**
   * GET /api/billing/admin/margin/:tenantID - Fetch a tenant's revenue/cost margin report (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param tenantID - The owning tenant.
   * @returns The margin report.
   */
  public async GetAdminMargin(signal: AbortSignal, tenantID: string): Promise<IMarginReportDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `admin/margin/${tenantID}`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch margin report');
    return await res.json();
  }
}
